import { openSupportChat } from "../../services/supportChat";
import React, { FC, ReactNode, useEffect, useMemo, useState } from "react";
import { css, styled } from "@mui/system";
import MainChart, {
  BaseMainChartEntry,
  MainChartBoringRoiData,
  MainChartData,
  MainChartDataAccuracy,
  MainChartTabSpecificData,
} from "./MainChart";
import {
  ExtendedEmailStatuses,
  GeneralStatuses,
  OrganizationStats,
  OutreachEmailStatuses,
  EmbedOrganization,
} from "../../types/organizations";
import {
  COLORS,
  FONT_FAMILIES,
  FONT_WEIGHTS,
  FONTS,
  RADII,
  SHADOWS,
  SPACINGS,
  StatusColors,
} from "../../common/consts";
import { calculateStatsPercentages } from "../../common/utils/stats";
import { makeShouldForwardProps } from "../../common/utils/ui";
import Text, { TextVariant } from "../../components/Display/Text";
import Card from "../../components/Display/Card";
import { ChartSkeleton, NumberBone } from "../../components/Display/Skeletons";
import Button, { ButtonVariants } from "../../components/Display/Button";
import { TimeframeOption } from "./Insights";
import { buildOutreachProspectUrl } from "../../common/utils/outreach";

const StatCard = styled("div")`
  border-radius: ${RADII.md};
  min-width: 9rem;
  line-height: 1.5rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
`;

const TitleText = styled(Text)`
  color: var(--color-foregroundBrand, ${COLORS.foregroundBrand});
  font: ${FONTS.sm};
  font-weight: ${FONT_WEIGHTS.medium};
  letter-spacing: 0.025rem;
`;

const SubtitleText = styled(Text)`
  color: var(--color-foregroundBrand, ${COLORS.foregroundBrand});
  font-family: ${FONT_FAMILIES.regular};
  font-size: 0.875rem;
  font-weight: ${FONT_WEIGHTS.regular};
  line-height: 1.25rem;
`;

const NoPaddingCard = styled(Card)`
  padding: ${SPACINGS.none};
`;

const ChartContainer = styled("div")`
  display: flex;
  align-items: stretch;
  border-left: 1px solid var(--color-borderPrimary, ${COLORS.borderPrimary});
  overflow: hidden;
`;

const PaddingDiv = styled("div")`
  padding: ${SPACINGS.lg};
`;

const shouldForwardProp = makeShouldForwardProps<ProspectsTabSectionProps>([
  "active",
]);
const ProspectsTabSection = styled(
  ({ active, ...rest }: ProspectsTabSectionProps) => <PaddingDiv {...rest} />,
  { shouldForwardProp }
)`
  padding: ${SPACINGS.xl};
  border-bottom: 1px solid var(--color-backgroundBrand, ${COLORS.backgroundBrand});
  cursor: pointer;

  &:hover {
    background-color: var(--color-backgroundBrandTabHover, ${COLORS.backgroundBrandTabHover});
  }

  /* Outreach's own selected stat treatment: brand fill with a brand rule. */
  ${({ active }) =>
    active &&
    css`
      box-shadow: ${SHADOWS.inset} var(--crm-accent, ${SHADOWS.brand});
      background-color: var(--color-backgroundBrandLight, ${COLORS.backgroundBrandLight});
      color: var(--color-foregroundBrand, ${COLORS.foregroundBrand});

      &:hover {
        background-color: var(--color-backgroundBrandLight, ${COLORS.backgroundBrandLight});
      }
    `}
`;

const NumberText = styled(Text)`
  margin: 0px;
  font-family: ${FONT_FAMILIES.titles};
  font-size: 1.5rem;
  font-weight: ${FONT_WEIGHTS.semibold};
  line-height: 2.25rem;
  color: var(--color-foregroundBrand, ${COLORS.foregroundBrand});
`;

const ClickableNumberText = styled(NumberText)`
  cursor: pointer;
  transition: opacity 0.2s;
  display: inline-block;
  padding: 0;
  margin: 0;

  &:hover {
    opacity: 0.7;
  }
`;

const ClickableSubtitleText = styled(SubtitleText)`
  cursor: pointer;
  transition: opacity 0.2s;
  display: inline-block;
  padding: 0;
  margin: 0;

  &:hover {
    opacity: 0.7;
  }
`;

interface ProspectTabManagerProps {
  globalStats: OrganizationStats;
  stats: MainChartData;
  isLoading?: boolean;
  organization?: EmbedOrganization | null;
  timeframe?: TimeframeOption;
}

export enum TabSections {
  ProspectsVerified = "ProspectsVerified",
  EmailDataAccuracy = "EmailDataAccuracy",
  BoringRoi = "BoringRoi",
}

interface ProspectsTabSectionProps {
  className?: string;
  active?: boolean;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  children?: ReactNode;
}

const filterStatsByTab = (
  stats: MainChartData,
  selectedTab: TabSections,
  isEnrichmentEnabled: boolean
): MainChartTabSpecificData => {
  return stats.map((dailyStat) => {
    const percentages = calculateStatsPercentages({
      prospectsChecked: dailyStat.prospectsChecked,
      valid: dailyStat[OutreachEmailStatuses.Valid],
      invalid: dailyStat[OutreachEmailStatuses.Invalid],
      questionable: dailyStat[OutreachEmailStatuses.Questionable],
      bouncesPrevented: dailyStat[ExtendedEmailStatuses.BouncesPrevented],
      enrichedByBoring: dailyStat[ExtendedEmailStatuses.EnrichedByBoring],
    });

    switch (selectedTab) {
      case TabSections.ProspectsVerified: {
        const prospectsChecked: BaseMainChartEntry = {
          percentages,
          date: dailyStat.date,
          [GeneralStatuses.ProspectsChecked]: dailyStat.prospectsChecked,
        };

        return prospectsChecked;
      }

      case TabSections.BoringRoi: {
        const boringRoi: MainChartBoringRoiData = {
          percentages,
          date: dailyStat.date,
          [OutreachEmailStatuses.Invalid]: dailyStat.invalid,
          ...(isEnrichmentEnabled && {
            [ExtendedEmailStatuses.EnrichedByBoring]: dailyStat.enrichedByBoring,
          }),
          [ExtendedEmailStatuses.BouncesPrevented]: dailyStat.bouncesPrevented,
        };

        return boringRoi;
      }

      case TabSections.EmailDataAccuracy:
      default: {
        const mainChartData: MainChartDataAccuracy = {
          percentages,
          date: dailyStat.date,
          [GeneralStatuses.ProspectsChecked]: dailyStat.prospectsChecked,
          [OutreachEmailStatuses.Valid]: dailyStat.valid,
          [OutreachEmailStatuses.Invalid]: dailyStat.invalid,
          [OutreachEmailStatuses.Questionable]: dailyStat.questionable,
        };
        return mainChartData;
      }
    }
  });
};

const ProspectsTabManager: FC<ProspectTabManagerProps> = ({
  globalStats,
  isLoading,
  stats,
  organization,
  timeframe,
}) => {
  const [hoveredOnCard, setHoveredOnCard] = useState<TabSections | null>(null);

  const [globalStatsPercentage, setGlobalStatsPercentage] =
    useState<OrganizationStats>({
      prospectsChecked: 0,
      enrichedByBoring: 0,
      invalid: 0,
      valid: 0,
      questionable: 0,
      bouncesPrevented: 0,
    });

  const [selectedTab, setSelectedTab] = useState<TabSections>(
    TabSections.EmailDataAccuracy
  );
  const [chartKey, setChartKey] = useState(0);

  const forceRedraw = () => {
    setChartKey((prevKey) => prevKey + 1); // Increment key to force re-render
  };

  const isEnrichmentEnabled = organization?.isEnrichmentEnabled ?? false;

  const statsToDisplay: MainChartTabSpecificData = useMemo(
    () => filterStatsByTab(stats, selectedTab, isEnrichmentEnabled),
    [stats, selectedTab, isEnrichmentEnabled]
  );

  useEffect(() => {
    setGlobalStatsPercentage(calculateStatsPercentages(globalStats));
  }, [globalStats]);

  const handleChangeTab = (tab: TabSections) => {
    setSelectedTab(tab);
    forceRedraw();
  };

  const handleNumberClick = (
    e: React.MouseEvent,
    status: OutreachEmailStatuses | ExtendedEmailStatuses | null = null,
    isBouncesPrevented = false
  ): void => {
    // Stop event propagation so clicking the number doesn't trigger the card's onClick
    e.stopPropagation();

    if (!organization || !timeframe) {
      return;
    }

    const url = buildOutreachProspectUrl(
      organization,
      timeframe.startDate,
      timeframe.endDate,
      status,
      isBouncesPrevented
    );

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleOpenEmbeddedChat = (e: React.MouseEvent): void => {
    e.stopPropagation();
    openSupportChat();
  };

  const displayValues = useMemo(() => {
    const valuesToReturn = {
      prospectsChecked: `${globalStats.prospectsChecked}`,
      valid: `${globalStatsPercentage.valid}%`,
      invalid: `${globalStatsPercentage.invalid}%`,
      questionable: `${globalStatsPercentage.questionable}%`,
      enrichedByBoring: `${globalStatsPercentage.enrichedByBoring}%`,
      bouncesPrevented: `${globalStatsPercentage.bouncesPrevented}`,
    };
    switch (hoveredOnCard) {
      case TabSections.EmailDataAccuracy:
        valuesToReturn[OutreachEmailStatuses.Valid] = `${globalStats.valid}`;
        valuesToReturn[OutreachEmailStatuses.Invalid] =
          `${globalStats.invalid}`;
        valuesToReturn[OutreachEmailStatuses.Questionable] =
          `${globalStats.questionable}`;
        break;
      case TabSections.BoringRoi:
        valuesToReturn[ExtendedEmailStatuses.EnrichedByBoring] =
          `${globalStats.enrichedByBoring}`;
        valuesToReturn[ExtendedEmailStatuses.BouncesPrevented] =
          `${globalStats.bouncesPrevented}`;
        break;
    }
    return valuesToReturn;
  }, [hoveredOnCard, globalStats, globalStatsPercentage]);

  return (
    <NoPaddingCard className="d-flex">
      <div>
        <ProspectsTabSection
          active={selectedTab === TabSections.ProspectsVerified}
          onClick={() => handleChangeTab(TabSections.ProspectsVerified)}
          onMouseEnter={() => setHoveredOnCard(TabSections.ProspectsVerified)}
          onMouseLeave={() => setHoveredOnCard(null)}
        >
          <TitleText variant={TextVariant.sm} className="text-uppercase mb-1">
            Prospects verified
          </TitleText>
          <StatCard>
            <ClickableNumberText
              variant={TextVariant.xl}
              color={COLORS.foregroundBrand}
              onClick={(e) => handleNumberClick(e, null)}
            >
              {isLoading ? <NumberBone /> : displayValues.prospectsChecked}
            </ClickableNumberText>
          </StatCard>
          <ClickableSubtitleText
            variant={TextVariant.sm}
            onClick={(e) => handleNumberClick(e, null)}
          >
            Total prospects verified
          </ClickableSubtitleText>
        </ProspectsTabSection>
        <ProspectsTabSection
          active={selectedTab === TabSections.EmailDataAccuracy}
          onClick={() => handleChangeTab(TabSections.EmailDataAccuracy)}
          onMouseEnter={() => setHoveredOnCard(TabSections.EmailDataAccuracy)}
          onMouseLeave={() => setHoveredOnCard(null)}
          className="d-flex flex-column gap-4"
        >
          <TitleText variant={TextVariant.sm} className="text-uppercase">
            Email data accuracy
          </TitleText>
          <div className="d-flex flex-row gap-4 flex-wrap">
            <StatCard>
              <ClickableNumberText
                variant={TextVariant.xl}
                color={StatusColors[OutreachEmailStatuses.Valid]}
                onClick={(e) => handleNumberClick(e, OutreachEmailStatuses.Valid)}
              >
                {isLoading ? <NumberBone /> : displayValues.valid}
              </ClickableNumberText>
              <ClickableSubtitleText
                variant={TextVariant.sm}
                onClick={(e) => handleNumberClick(e, OutreachEmailStatuses.Valid)}
              >
                Valid
              </ClickableSubtitleText>
            </StatCard>
            <StatCard>
              <ClickableNumberText
                variant={TextVariant.xl}
                color={StatusColors[OutreachEmailStatuses.Invalid]}
                onClick={(e) => handleNumberClick(e, OutreachEmailStatuses.Invalid)}
              >
                {isLoading ? <NumberBone /> : displayValues.invalid}
              </ClickableNumberText>
              <ClickableSubtitleText
                variant={TextVariant.sm}
                onClick={(e) => handleNumberClick(e, OutreachEmailStatuses.Invalid)}
              >
                Invalid
              </ClickableSubtitleText>
            </StatCard>
          </div>
          <div className="d-flex flex-row gap-4">
            <StatCard>
              <ClickableNumberText
                variant={TextVariant.xl}
                color={StatusColors[OutreachEmailStatuses.Questionable]}
                onClick={(e) => handleNumberClick(e, OutreachEmailStatuses.Questionable)}
              >
                {isLoading ? <NumberBone /> : displayValues.questionable}
              </ClickableNumberText>
              <ClickableSubtitleText
                variant={TextVariant.sm}
                onClick={(e) => handleNumberClick(e, OutreachEmailStatuses.Questionable)}
              >
                Questionable
              </ClickableSubtitleText>
            </StatCard>
            {/* Missing email metric removed - feature not working */}
          </div>
        </ProspectsTabSection>
        <ProspectsTabSection
          active={selectedTab === TabSections.BoringRoi}
          onClick={() => handleChangeTab(TabSections.BoringRoi)}
          onMouseEnter={() => setHoveredOnCard(TabSections.BoringRoi)}
          onMouseLeave={() => setHoveredOnCard(null)}
          className="d-flex flex-column"
        >
          <TitleText variant={TextVariant.sm} className="text-uppercase mb-2">
            Boring ROI
          </TitleText>
          <div className="d-flex flex-row gap-4">
            <StatCard>
              <ClickableNumberText
                variant={TextVariant.xl}
                onClick={(e) =>
                  handleNumberClick(e, ExtendedEmailStatuses.BouncesPrevented, true)
                }
              >
                {isLoading ? <NumberBone /> : displayValues.bouncesPrevented}
              </ClickableNumberText>
              <ClickableSubtitleText
                variant={TextVariant.sm}
                onClick={(e) =>
                  handleNumberClick(e, ExtendedEmailStatuses.BouncesPrevented, true)
                }
              >
                Bounces prevented
              </ClickableSubtitleText>
            </StatCard>
            {isEnrichmentEnabled ? (
              <StatCard>
                <ClickableNumberText
                  variant={TextVariant.xl}
                  color={StatusColors[ExtendedEmailStatuses.EnrichedByBoring]}
                  onClick={(e) =>
                    handleNumberClick(e, ExtendedEmailStatuses.EnrichedByBoring)
                  }
                >
                  {isLoading ? <NumberBone /> : displayValues.enrichedByBoring}
                </ClickableNumberText>
                <ClickableSubtitleText
                  variant={TextVariant.xl}
                  onClick={(e) =>
                    handleNumberClick(e, ExtendedEmailStatuses.EnrichedByBoring)
                  }
                >
                  Emails enriched
                </ClickableSubtitleText>
              </StatCard>
            ) : (
              <StatCard>
                <Button
                  variant={ButtonVariants.brand}
                  onClick={handleOpenEmbeddedChat}
                  style={{ 
                    marginTop: "-10px",
                    marginBottom: "9px",
                    alignSelf: "flex-start"
                  }}
                >
                  Upgrade
                </Button>
                <SubtitleText 
                  variant={TextVariant.sm} 
                  style={{ 
                    marginTop: 0,
                    marginBottom: 0
                  }}
                >
                  Emails enriched
                </SubtitleText>
              </StatCard>
            )}
          </div>
        </ProspectsTabSection>
      </div>
      <ChartContainer className="position-relative flex-grow-1 py-2 pl-3 pr-0">
        {isLoading ? (
          /* The chart's own frame — legend, axes, plot — while the stats load. */
          <ChartSkeleton series={isEnrichmentEnabled ? 4 : 3} />
        ) : (
          <MainChart
            key={chartKey}
            data={statsToDisplay}
            selectedTab={selectedTab}
            isEnrichmentEnabled={isEnrichmentEnabled}
          />
        )}
      </ChartContainer>
    </NoPaddingCard>
  );
};

export default ProspectsTabManager;
